import { observer } from "mobx-react-lite";
import { useContext, useEffect, useState } from "react";
import { AppStoreContext } from "../store/app-store";

const DebugPanel = observer(() => {
    const [isOpen, setIsOpen] = useState(false);
    const [logs, setLogs] = useState<string[]>([]);
    const store = useContext(AppStoreContext);

    useEffect(() => {
        // Intercept console.log
        const originalLog = console.log;
        const originalError = console.error;

        console.log = (...args) => {
            originalLog(...args);
            const message = args.map(arg =>
                typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
            ).join(' ');
            // Defer setState to next event loop to avoid "setState during render" warnings
            setTimeout(() => {
                setLogs(prev => [...prev.slice(-49), `[LOG] ${new Date().toLocaleTimeString()}: ${message}`]);
            }, 0);
        };

        console.error = (...args) => {
            originalError(...args);
            const message = args.map(arg =>
                typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
            ).join(' ');
            // Defer setState to next event loop to avoid "setState during render" warnings
            setTimeout(() => {
                setLogs(prev => [...prev.slice(-49), `[ERROR] ${new Date().toLocaleTimeString()}: ${message}`]);
            }, 0);
        };

        // Keyboard shortcut: Ctrl+Shift+D or Cmd+Shift+D
        const handleKeyPress = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyD') {
                e.preventDefault();
                setIsOpen(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            console.log = originalLog;
            console.error = originalError;
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, []);
    if (isOpen) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    color: '#00ff00',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    padding: '20px',
                    zIndex: 10000,
                    overflow: 'auto',
                }}
            >
                <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => setIsOpen(false)}
                        style={{
                            padding: '8px 12px',
                            backgroundColor: '#ff4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    >
                        ✕ Close
                    </button>
                    <button
                        onClick={() => setLogs([])}
                        style={{
                            padding: '8px 12px',
                            backgroundColor: '#444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    >
                        🗑 Clear Logs
                    </button>
                    <button
                        onClick={() => window.__DEBUG__?.logState()}
                        style={{
                            padding: '8px 12px',
                            backgroundColor: '#0066cc',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    >
                        📊 Log State
                    </button>
                </div>

                <div style={{ marginBottom: '20px', borderBottom: '2px solid #00ff00', paddingBottom: '10px' }}>
                    <h3 style={{ margin: '0 0 10px 0' }}>🎯 Current State</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px 15px' }}>
                        <strong>Status:</strong>
                        <span>{store.status}</span>

                        <strong>Locale:</strong>
                        <span>{store.locale}</span>

                        <strong>isTranslationsLoading:</strong>
                        <span style={{ color: store.isTranslationsLoading ? '#ff0000' : '#00ff00' }}>
                        {store.isTranslationsLoading ? '⚠️ TRUE (LOADING)' : '✅ FALSE'}
                    </span>

                        <strong>isPaired:</strong>
                        <span>{store.isPaired ? 'Yes' : 'No'}</span>

                        <strong>Messages Count:</strong>
                        <span>{Object.keys(store.messages || {}).length}</span>

                        <strong>Context Keys:</strong>
                        <span>{Object.keys(store.context || {}).join(', ')}</span>

                        <strong>customerDefaultLanguage:</strong>
                        <span>{store.context.customerDefaultLanguage || 'undefined'}</span>
                    </div>
                </div>

                <div>
                    <h3 style={{ margin: '0 0 10px 0' }}>📜 Console Logs</h3>
                    <div style={{
                        backgroundColor: '#0a0a0a',
                        padding: '10px',
                        borderRadius: '4px',
                        maxHeight: '400px',
                        overflow: 'auto',
                    }}>
                        {logs.length === 0 ? (
                            <div style={{ color: '#666' }}>No logs yet...</div>
                        ) : (
                            logs.map((log, index) => (
                                <div
                                    key={index}
                                    style={{
                                        marginBottom: '5px',
                                        whiteSpace: 'pre-wrap',
                                        color: log.includes('[ERROR]') ? '#ff4444' : '#00ff00',
                                    }}
                                >
                                    {log}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div style={{ marginTop: '10px', color: '#666' }}>
                    Press Ctrl+Shift+D (or Cmd+Shift+D) to toggle this panel
                </div>
            </div>
        );
    }
    return null;
});

export default DebugPanel;
